import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Button } from "./button";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Heading2,
  Code,
  Undo,
  Redo,
} from "lucide-react";
import { cn } from "@/lib/utils";

type MenuBarButton =
  | "bold"
  | "italic"
  | "heading"
  | "bulletList"
  | "orderedList"
  | "blockquote"
  | "code"
  | "undo"
  | "redo";

interface MenuBarProps {
  editor: Editor | null;
  buttons?: MenuBarButton[];
}

const MenuBar = ({ editor, buttons }: MenuBarProps) => {
  if (!editor) {
    return null;
  }

  const btnCn = "h-6 w-6 p-0";

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-t bg-background">
      {buttons?.includes("bold") && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={cn(btnCn, editor.isActive("bold") && "bg-accent")}
          title="Bold"
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
      )}
      {buttons?.includes("italic") && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={cn(btnCn, editor.isActive("italic") && "bg-accent")}
          title="Italic"
        >
          <Italic className="h-3.5 w-3.5" />
        </Button>
      )}
      {buttons?.includes("heading") && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          className={cn(
            btnCn,
            editor.isActive("heading", { level: 2 }) && "bg-accent",
          )}
          title="Heading"
        >
          <Heading2 className="h-3.5 w-3.5" />
        </Button>
      )}
      {buttons?.includes("bulletList") && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={cn(btnCn, editor.isActive("bulletList") && "bg-accent")}
          title="Bullet List"
        >
          <List className="h-3.5 w-3.5" />
        </Button>
      )}
      {buttons?.includes("orderedList") && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={cn(btnCn, editor.isActive("orderedList") && "bg-accent")}
          title="Numbered List"
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </Button>
      )}
      {buttons?.includes("blockquote") && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={cn(btnCn, editor.isActive("blockquote") && "bg-accent")}
          title="Quote"
        >
          <Quote className="h-3.5 w-3.5" />
        </Button>
      )}
      {buttons?.includes("code") && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={cn(btnCn, editor.isActive("code") && "bg-accent")}
          title="Code"
        >
          <Code className="h-3.5 w-3.5" />
        </Button>
      )}
      {buttons?.includes("undo") && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          className={btnCn}
          title="Undo"
        >
          <Undo className="h-3.5 w-3.5" />
        </Button>
      )}
      {buttons?.includes("redo") && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          className={btnCn}
          title="Redo"
        >
          <Redo className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
};

export interface MarkdownEditorProps {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  buttons?: MenuBarButton[];
  readOnly?: boolean;
  autoExpand?: boolean;
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  buttons = [
    "bold",
    "italic",
    "heading",
    "bulletList",
    "orderedList",
    "blockquote",
    "code",
    "undo",
    "redo",
  ],
  readOnly = false,
  autoExpand = true,
}: MarkdownEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none focus:outline-none px-3 py-2",
          autoExpand ? "min-h-[2lh]" : readOnly ? "min-h-[60px]" : "min-h-[150px]",
        ),
      },
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  return (
    <div
      className={cn(
        "group border border-input",
        readOnly ? "bg-muted/30" : "bg-background",
      )}
    >
      <EditorContent editor={editor} className="markdown-editor-content" />
      {!readOnly && (
        <div className="opacity-0 group-focus-within:opacity-100 transition-opacity duration-150">
          <MenuBar editor={editor} buttons={buttons} />
        </div>
      )}
      <style>{`
        .markdown-editor-content h2 {
          font-size: 1.25em;
          font-weight: 600;
          margin: 0.5em 0 0.25em;
        }
        .markdown-editor-content ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .markdown-editor-content ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .markdown-editor-content li {
          margin: 0.25em 0;
        }
        .markdown-editor-content li p {
          margin: 0;
        }
        .markdown-editor-content .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: var(--color-muted-foreground);
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
}
